import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { LoadingGroup, Pager, Spinner, Table, useSimpleTableDataSource } from "capitalrx-components-2.0/lib/general";
import { Grid, Shelf, Stack } from "capitalrx-components-2.0/lib/layout";
import { Subheading } from "capitalrx-components-2.0/lib/typography";

import { UniformRowColorTable } from "@/App/components";

import { useGetEligibilityMemberSkipMonthsQuery, useGetEligibilityMemberSkipMonthStatsQuery } from "../../api";
import { DisplayField } from "../../components/DisplayField";
import { SkipMonthsBaseType } from "../../interface";

export const SkipMonths = () => {
  const { t: translate } = useTranslation();

  const { data: eligibilityMemberSkipMonthsData, isLoading: skipMonthsDataIsLoading } = useGetEligibilityMemberSkipMonthsQuery();
  const { data: eligibilityMemberSkipMonthStatsData, isLoading: skipMonthStatsDataIsLoading } = useGetEligibilityMemberSkipMonthStatsQuery();

  const skipMonthsRows = useMemo(() => {
    return (eligibilityMemberSkipMonthsData?.results || []).map((skipMonths: SkipMonthsBaseType) => {
      return {
        id: skipMonths.skip_month.id,
        data: {
          ...skipMonths.skip_month,
        },
      };
    });
  }, [eligibilityMemberSkipMonthsData]);

  const skipMonthsRowsDataSource = useSimpleTableDataSource(skipMonthsRows, { pageSize: 10 });

  return (
    <LoadingGroup loading={skipMonthsDataIsLoading || skipMonthStatsDataIsLoading}>
      <LoadingGroup.Placeholder
        fallback={
          <Shelf>
            <Spinner size="large" />
          </Shelf>
        }
      >
        <Stack>
          <Stack spacing="large">
            <Subheading>{translate("ELIGIBILITY.skipMonths")}</Subheading>
            <Grid>
              <Grid.Span columns={2}>
                <DisplayField title={translate("ELIGIBILITY.consecutiveMonthsSkipped")} isTitleSemibold={true}>
                  {eligibilityMemberSkipMonthStatsData?.consecutive_months_skipped}
                  {translate("ELIGIBILITY.of")}
                  {eligibilityMemberSkipMonthStatsData?.plan_year_maximum_consecutive_skip_months}
                </DisplayField>
              </Grid.Span>
              <Grid.Span columns={2}>
                <DisplayField title={translate("ELIGIBILITY.totalMonthsSkipped")} isTitleSemibold={true}>
                  {eligibilityMemberSkipMonthStatsData?.total_months_skipped}
                  {translate("ELIGIBILITY.of")}
                  {eligibilityMemberSkipMonthStatsData?.plan_year_maximum_skip_months}
                </DisplayField>
              </Grid.Span>
            </Grid>

            <UniformRowColorTable
              dataSource={skipMonthsRowsDataSource}
              footer={<Pager dataSource={skipMonthsRowsDataSource} testId="skip_months_table_pagination" />}
            >
              <Table.Date name="created_at" title={translate("ELIGIBILITY.dateRequested")} sortable="created_at" initialWidth="span" format="MM/dd/yyyy" />
              <Table.Date name="month_skipped" title={translate("ELIGIBILITY.monthSkipped")} sortable="month_skipped" initialWidth="span" format="MM/dd/yyyy" />
              <Table.Text name="hours_consumed" title={translate("ELIGIBILITY.hoursConsumed")} sortable="hours_consumed" initialWidth="span" />
            </UniformRowColorTable>
          </Stack>
        </Stack>
      </LoadingGroup.Placeholder>
    </LoadingGroup>
  );
};
